import React, { useEffect, useRef, useCallback } from "react";
import { X } from "lucide-react";

type ModalSize = "sm" | "md" | "lg" | "xl";

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  size?: ModalSize;
  showClose?: boolean;
}

const sizeClasses: Record<ModalSize, string> = {
  sm: "max-w-sm",
  md: "max-w-lg",
  lg: "max-w-2xl",
  xl: "max-w-4xl",
};

export const Modal: React.FC<ModalProps> = ({
  open,
  onClose,
  title,
  children,
  size = "md",
  showClose = true,
}) => {
  const overlayRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  /* focus trap */
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
        return;
      }
      if (e.key !== "Tab" || !contentRef.current) return;

      const focusable = contentRef.current.querySelectorAll<HTMLElement>(
        'a[href],button:not([disabled]),textarea,input,select,[tabindex]:not([tabindex="-1"])',
      );
      if (focusable.length === 0) return;

      // `noUncheckedIndexedAccess`: el acceso por índice puede ser undefined.
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first || !last) return;

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    },
    [onClose],
  );

  useEffect(() => {
    if (!open) return;
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, handleKeyDown]);

  /*
   * ── El auto-enfoque corre SOLO al ABRIR (Anahí, 27-ago — bug real) ──
   * Estaba junto al listener de teclado, con `handleKeyDown` en las
   * dependencias. Como el padre pasa un `onClose` inline
   * (`() => setOpen(false)`), ese callback cambia de identidad en CADA
   * render — y cada tecla del formulario hacía re-correr el efecto y su
   * `focus()`, que saltaba al primer elemento enfocable (el botón ✕) y
   * te SACABA del campo tras una sola letra. Con `[open]` a secas, el
   * foco se coloca una vez, al abrir, y no vuelve a moverse solo.
   */
  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(() => {
      const el = contentRef.current?.querySelector<HTMLElement>(
        'a[href],button:not([disabled]),textarea,input,select,[tabindex]:not([tabindex="-1"])',
      );
      el?.focus();
    }, 0);
    return () => clearTimeout(timer);
  }, [open]);

  if (!open) return null;

  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 z-50 flex items-center justify-center bg-ds-ink/35 backdrop-blur-sm p-4 transition-opacity"
      aria-modal="true"
      role="dialog"
      onClick={(e) => {
        if (e.target === overlayRef.current) onClose();
      }}
    >
      <div
        ref={contentRef}
        className={[
          "w-full rounded-ds-4xl bg-ds-surface shadow-ds-float border border-ds-border font-manrope",
          "animate-[apple-fade-in_0.25s_cubic-bezier(0.25,0.1,0.25,1)]",
          sizeClasses[size],
        ].join(" ")}
      >
        {(title || showClose) && (
          <div className="flex items-center justify-between border-b border-ds-border-soft px-6 py-4">
            {title && (
              <h2 className="font-semibold text-[22px] leading-tight text-ds-ink">{title}</h2>
            )}
            {showClose && (
              <button
                type="button"
                onClick={onClose}
                aria-label="Cerrar"
                className="ml-auto rounded-full bg-ds-mist p-1.5 text-ds-ink-3 hover:bg-ds-lavender-bg hover:text-ds-ink transition-all duration-200"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        )}
        <div className="px-6 py-5">{children}</div>
      </div>
    </div>
  );
};
